import { GameMode, KO_RULES, MatchPhase, SETTINGS_OPTIONS, TICK_RATE, Team, type CharacterManifest, type RoomSettings } from "@arena/shared";
import { MAP_CHOICES, getArena } from "@arena/arena-data";
import { TEAM_NAMES, fighterCss } from "../palette.ts";

export interface LobbyPlayer {
  id: string;
  name: string;
  color: number;
  ready: boolean;
  connected: boolean;
  characterId: string;
  /** `Team`; 0 in a free-for-all. */
  team: number;
  /** A sparring bot run by the server. */
  bot: boolean;
}

export interface LobbyView {
  phase: string;
  code: string;
  /** The room's map (D49). */
  arenaId: string;
  localId: string;
  hostId: string;
  players: LobbyPlayer[];
  settings: RoomSettings;
  /** One line about the last match, shown until the next one starts. */
  lastMatch: string;
}

/** Host settings with many values are drop-down lists; mode and teams are two buttons each. */
type ListSetting = "maxPlayers" | "targetScore" | "durationSec";
type ChoiceSetting = "gameMode" | "teams";

const SETTING_LABELS: Record<keyof RoomSettings, (v: number) => string> = {
  gameMode: (v) => v === GameMode.KO ? "Arena KO" : "Slam",
  maxPlayers: (v) => String(v),
  targetScore: (v) => String(v),
  durationSec: (v) => `${v / 60} min`,
  teams: (v) => (v ? "Squadre" : "Tutti contro tutti"),
};

const MODE_TEXT = {
  ffa: "Tutti contro tutti: 1 punto per ogni avversario preso dalla Super Slam.",
  teams: "Due squadre, niente fuoco amico: i punti della Super Slam vanno alla squadra. Si vince a 5 punti per giocatore (15 in 3v3, 20 in 4v4).",
};

/**
 * The register on the museum's curator's desk (GDD §6.3–6.4, §7.1, D49):
 * shareable code, players with host and the champion each took, the host's
 * settings and start. Closed, the museum is on screen with its bar (code,
 * invite, leave); the champions are taken from their statues, not here.
 * Every action is a request; the replicated state is the only source of what
 * is shown.
 */
export class LobbyPanel {
  onStart: () => void = () => {};
  onLeave: () => void = () => {};
  onShare: () => void = () => {};
  onSettings: (patch: Partial<RoomSettings>) => void = () => {};
  onKick: (sessionId: string) => void = () => {};
  onTeam: (team: number) => void = () => {};
  onShuffle: () => void = () => {};
  onAddBot: () => void = () => {};
  onMap: (arenaId: string) => void = () => {};

  private readonly root = element("lobby");
  /** The right end of the header, where `docked` sits while the lobby is open. */
  private readonly headEnd = element("lobby-head-end");
  private readonly trainingMenu = element("training-menu");
  private readonly code = element("lobby-code");
  private readonly barCode = element("museum-code");
  private readonly count = element("lobby-count");
  private readonly players = element("lobby-players");
  private readonly last = element("lobby-last");
  private readonly hint = element("lobby-hint");
  private readonly start = element<HTMLButtonElement>("lobby-start");
  private readonly shuffle = element<HTMLButtonElement>("lobby-shuffle");
  private readonly addBot = element<HTMLButtonElement>("lobby-addbot");
  private readonly mode = element("lobby-mode");
  private readonly selects: Record<ListSetting, HTMLSelectElement> = {
    maxPlayers: element<HTMLSelectElement>("set-maxPlayers"),
    targetScore: element<HTMLSelectElement>("set-targetScore"),
    durationSec: element<HTMLSelectElement>("set-durationSec"),
  };
  /** The map, chosen in the register with the rest (D49). */
  private readonly maps = element("set-map");
  private readonly choices: Record<ChoiceSetting, HTMLElement> = {
    gameMode: element("set-gameMode"),
    teams: element("set-teams"),
  };
  /** Closed: the museum is on screen. It opens from the curator's desk. */
  private folded = true;
  private rendered = "";
  private readonly championNames: ReadonlyMap<string, string>;

  /** Floats over the screen elsewhere; while the lobby is open it sits in the header, where it cannot cover a button. */
  private readonly docked: HTMLElement | undefined;

  /** `docked`: the audio controls. */
  constructor(characters: readonly CharacterManifest[], docked?: HTMLElement) {
    this.docked = docked;
    this.championNames = new Map(characters.map((c) => [c.id, c.displayName]));
    element("lobby-share").addEventListener("click", () => this.onShare());
    element("museum-share").addEventListener("click", () => this.onShare());
    element("training-leave").addEventListener("click", () => this.onLeave());
    element("lobby-hide").addEventListener("click", () => this.setFolded(true));
    this.start.addEventListener("click", () => this.onStart());
    this.shuffle.addEventListener("click", () => this.onShuffle());
    this.addBot.addEventListener("click", () => this.onAddBot());

    for (const key of Object.keys(this.selects) as ListSetting[]) {
      const select = this.selects[key];
      select.replaceChildren(
        ...SETTINGS_OPTIONS[key].map((value) => new Option(SETTING_LABELS[key](value), String(value))),
      );
      select.addEventListener("change", () => this.onSettings({ [key]: Number(select.value) }));
    }
    for (const key of Object.keys(this.choices) as ChoiceSetting[]) {
      this.choices[key].replaceChildren(...SETTINGS_OPTIONS[key].map((value) => {
        const button = document.createElement("button");
        button.type = "button";
        button.setAttribute("role", "radio");
        button.dataset.value = String(value);
        button.textContent = SETTING_LABELS[key](value);
        button.addEventListener("click", () => {
          if (button.getAttribute("aria-checked") !== "true") this.onSettings({ [key]: value });
        });
        return button;
      }));
    }

    this.maps.replaceChildren(...MAP_CHOICES.map((id) => {
      const button = document.createElement("button");
      button.type = "button";
      button.setAttribute("role", "radio");
      button.dataset.value = id;
      button.textContent = getArena(id).displayName;
      button.addEventListener("click", () => {
        if (button.getAttribute("aria-checked") !== "true") this.onMap(id);
      });
      return button;
    }));

    this.players.addEventListener("click", (e) => {
      const target = (e.target as HTMLElement).closest<HTMLButtonElement>("button.kick, button.switch");
      if (target?.classList.contains("kick") && target.dataset.id) this.onKick(target.dataset.id);
      if (target?.classList.contains("switch")) this.onTeam(Number(target.dataset.team));
    });
  }

  /** Called every frame while in a room; renders only when something changed. */
  update(view: LobbyView | null): void {
    const inLobby = view !== null && view.phase === MatchPhase.Lobby;
    // Back from a match, the museum first; the register opens from the desk.
    if (!inLobby) this.folded = true;
    document.body.classList.toggle("in-lobby", inLobby && !this.folded);
    document.body.classList.toggle("in-training", inLobby && this.folded);
    this.root.hidden = !inLobby || this.folded;
    this.trainingMenu.hidden = !inLobby || !this.folded;
    this.dock(inLobby && !this.folded);
    if (!view || !inLobby) return;

    const key = JSON.stringify(view);
    if (key === this.rendered) return;
    this.rendered = key;

    const isHost = view.localId === view.hostId;
    this.code.textContent = view.code;
    this.barCode.textContent = view.code;
    this.count.textContent = `${view.players.length}/${view.settings.maxPlayers}`;
    this.renderPlayers(view, isHost);
    this.last.hidden = view.lastMatch === "";
    this.last.textContent = view.lastMatch;

    const teams = view.settings.teams === 1;
    for (const key of Object.keys(this.selects) as ListSetting[]) {
      const select = this.selects[key];
      select.value = String(key === "targetScore" && teams ? 5 : view.settings[key] ?? 0);
      // In a team match the goal follows the team size (D28).
      select.disabled = !isHost || (teams && key === "targetScore");
    }
    for (const button of this.maps.querySelectorAll("button")) {
      button.setAttribute("aria-checked", String(button.dataset.value === view.arenaId));
      button.disabled = !isHost;
    }
    for (const key of Object.keys(this.choices) as ChoiceSetting[]) {
      for (const button of this.choices[key].querySelectorAll("button")) {
        button.setAttribute("aria-checked", String(Number(button.dataset.value) === view.settings[key]));
        button.disabled = !isHost;
      }
    }
    const ko = view.settings.gameMode === GameMode.KO;
    this.mode.textContent = ko
      ? `Arena KO all'ultimo sangue: ${KO_RULES.maxHp} HP e nessun rientro; chi va KO guarda la partita o esce. Vince ${teams ? "l'ultima squadra" : "l'ultimo"} in piedi. Allo scadere, ${KO_RULES.overtimeTicks / TICK_RATE / 60} minuto di deathmatch tra i sopravvissuti, poi chi è ancora vivo pareggia.`
      : teams ? MODE_TEXT.teams : MODE_TEXT.ffa;
    // Arena KO has no score to reach: the points are not a setting there.
    this.selects.targetScore.closest("label")!.hidden = ko;
    this.shuffle.hidden = !(isHost && teams);
    this.addBot.hidden = !isHost || view.players.length >= view.settings.maxPlayers;

    // Picking a champion is one tap on a portrait, and it is also the confirmation.
    this.start.hidden = !isHost;
    this.start.disabled = view.players.filter(p=>p.connected).length < 2 || view.players.some(p=>!p.connected || !p.ready);
    this.hint.textContent = hintFor(view, isHost);
  }

  /** After leaving the room: the next one starts in its museum. */
  reset(): void {
    this.folded = true;
    document.body.classList.remove("in-lobby", "in-training");
    this.rendered = "";
    this.root.hidden = true;
    this.trainingMenu.hidden = true;
    this.dock(false);
  }

  /** The register is open over the museum. */
  get open(): boolean { return !this.folded; }

  /** The host's register, from the curator's desk (D49); everyone may read it. */
  openRegister(): void {
    this.setFolded(false);
  }

  private dock(open: boolean): void {
    const place = open ? this.headEnd : document.body;
    if (this.docked && this.docked.parentElement !== place) place.append(this.docked);
  }

  private setFolded(folded: boolean): void {
    this.folded = folded;
    this.rendered = "";
  }

  private renderPlayers(view: LobbyView, isHost: boolean): void {
    // Team match: Blue first, then Red, each in join order.
    const players = view.settings.teams === 1 ? [...view.players].sort((a, b) => a.team - b.team) : view.players;
    this.players.replaceChildren(
      ...players.map((p) => {
        const li = document.createElement("li");
        li.classList.toggle("self", p.id === view.localId);
        li.classList.toggle("offline", !p.connected);
        li.style.setProperty("--color", fighterCss(p.color, p.team));
        const name = document.createElement("span");
        name.className = "name";
        name.textContent = p.name;
        name.title = p.name;
        const meta=document.createElement("div");meta.className="player-meta";
        li.append(name,meta);
        // Until it picks, a player is a ghost (D49).
        const champion = p.ready || p.bot ? p.characterId : undefined;
        meta.append(tag(champion ? "champ" : "champ choosing", champion ? this.championNames.get(champion) ?? "?" : "fantasma"));
        if (p.team !== Team.None) meta.append(tag("team", TEAM_NAMES[p.team] ?? ""));
        if (p.id === view.localId && p.team !== Team.None) {
          const other = p.team === Team.Blue ? Team.Red : Team.Blue;
          const button = document.createElement("button");
          button.className = "switch";
          button.dataset.team = String(other);
          button.textContent = "⇄";
          button.title = `Passa ai ${TEAM_NAMES[other]}`;
          button.setAttribute("aria-label", `Passa ai ${TEAM_NAMES[other]}`);
          meta.append(button);
        }
        if (p.bot) meta.append(tag("bot", "bot"));
        else if (p.id === view.hostId) meta.append(tag("host", "host"));
        if (!p.connected) meta.append(tag("", "riconnessione…"));
        if (isHost && p.id !== view.localId && p.connected) {
          const kick = document.createElement("button");
          kick.className = "kick";
          kick.dataset.id = p.id;
          kick.textContent = "✕";
          kick.title = `Allontana ${p.name}`;
          kick.setAttribute("aria-label", `Allontana ${p.name}`);
          meta.append(kick);
        }
        return li;
      }),
    );
  }
}

function hintFor(view: LobbyView, isHost: boolean): string {
  const waiting = view.players.filter(p=>!p.connected || !p.ready).length;
  if (waiting) return waiting === 1 ? "C'è ancora un fantasma senza campione." : `Ci sono ancora ${waiting} fantasmi senza campione.`;
  if (view.players.length < 2) return "Invita un amico o aggiungi un bot.";
  return isHost ? "Tutti hanno un campione: avvia ora o aspetta altri giocatori."
    : "Tutti hanno un campione: aspetta che l’host avvii.";
}

function tag(kind: string, text: string): HTMLElement {
  const span = document.createElement("span");
  span.className = kind ? `tag ${kind}` : "tag";
  span.textContent = text;
  return span;
}

function element<T extends HTMLElement = HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`#${id} mancante in index.html`);
  return el as T;
}
